import type {
  AddCurrencyRequest,
  CurrencyRow,
  CurrencyTableResponse,
  FetchRatesResult,
  PostRevaluationRequest,
  RateHistoryPoint,
  RevaluationPreviewResponse,
  RevaluationResponse,
  SetManualRateRequest,
} from '@palaccount/contracts';
import { DEFAULT_PAGE_SIZE } from '@palaccount/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { ACCOUNTS_KEY } from '@/features/accounts/api';
import { throwApiError } from '@/lib/server-messages';
import { useBaseCurrency } from '@/lib/session';

/** The FIN-05 data layer — same shape as banking/accounts. */

export const FX_KEY = ['fx'] as const;

/**
 * `includeInactive` is the MD-11 maintenance screen's switch and nobody
 * else's: a picker asks without it and never sees a switched-off currency
 * (BR-DATA-06), while this screen asks with it so the row it just switched
 * off is still there to switch back on (§7's `Inactive → Active`).
 */
export function useCurrencyTable(includeInactive = false) {
  return useQuery({
    queryKey: [...FX_KEY, 'currencies', includeInactive],
    queryFn: async (): Promise<CurrencyTableResponse> => {
      const response = await api.fx.getCurrencyTable({
        query: includeInactive ? { includeInactive: 'true' } : {},
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

/**
 * THE CURRENCY CODES A PICKER MAY OFFER — the one list, for every screen that
 * asks a user to name a currency.
 *
 * It exists because four screens answered this question for themselves and all
 * four got it wrong in the same direction: two hard-coded six-code lists
 * (`partners/constants.ts`, `accounts/chart-page.tsx`) and two free-text
 * boxes. The hard-coded pair offered `EUR` after an admin switched EUR off, so
 * the product drew a choice the switch-off-only guard then answered with a
 * 409 — refusing what it had just offered.
 *
 * THREE SOURCES, in this order, and each is here for a rule:
 *
 *  1. **The base, first and always.** `CurrencyTableResponse.rows` NEVER
 *     contains the base (BR-FIN-05-07), so a caller that renders `rows` alone
 *     renders a company that cannot name its own money. GEN-107 gives every
 *     tenant exactly one base; it is never unpickable. The session's base is
 *     the fallback while the table is in flight, so the control is never
 *     briefly empty.
 *
 *  2. **The active rows.** Switched-off codes are already absent — the route
 *     filters them (BR-DATA-06: a switched-off record "must stop being offered
 *     on new ones"). That filter is the whole point of reading this table.
 *
 *  3. **`current`, if it is neither of those.** BR-MD-01-07's line between
 *     what a picker OFFERS and what it DISPLAYS. A partner denominated in EUR
 *     and locked by BR-MD-01-05 because documents exist must still display and
 *     still save after the tenant switches EUR off — otherwise the picker
 *     becomes the thing that refuses an edit the server would have allowed,
 *     and a control that renders blank over a real value writes the blank
 *     back. Appended, not inserted, so it never looks like an ordinary offer.
 *
 * Deliberately returns CODES rather than rows: every caller renders the code,
 * and a caller holding rows would have to re-derive (3) to keep a locked value
 * on screen — which is exactly the step each of the four skipped.
 */
export function useCurrencyPickerCodes(
  current?: string | null | undefined,
  options?: {
    /**
     * FALSE for a control that already says "base" another way — the chart of
     * accounts offers `<option value="">Base currency</option>`, where an empty
     * value means "not foreign-denominated". Listing the base code beside it
     * would give one fact two spellings, and they are NOT interchangeable
     * downstream: FIN-05's revaluation selects
     * `a.currency IS NOT NULL AND a.currency <> base`, so `''` and `'USD'` in a
     * USD company mean the same thing to the reader and different things to the
     * query. One spelling per fact.
     */
    includeBase?: boolean;
  },
): string[] {
  const { data: table } = useCurrencyTable();
  const sessionBase = useBaseCurrency();
  const baseCode = table?.base.code ?? sessionBase;

  const codes = [
    ...(options?.includeBase === false ? [] : [baseCode]),
    ...(table?.rows ?? []).map((row) => row.code),
  ];
  const held = current?.trim().toUpperCase();
  /*
   * THE APPEND `currencyNotOffered` DEPENDS ON. Removing it does not merely
   * drop a code from a dropdown — it turns the free-text warning below into a
   * scold aimed at users holding a currency they are entitled to hold. If this
   * line looks redundant, read that helper before deleting it;
   * currency-warning.test.tsx fails if you do.
   */
  if (held !== undefined && held !== '' && !codes.includes(held)) codes.push(held);
  return codes;
}

/**
 * "IS THIS CODE ONE THE TENANT ACTUALLY HAS?" — the free-text screens' warning
 * test, in one place because it is exactly one line and exactly one trap.
 *
 * The warehouse and bank-account currency boxes are free text, so
 * `<CurrencyField>` accepts anything typed; the strict half of
 * `currency-guard.ts` will refuse a code the tenant never added. Without this
 * the screen accepts in silence and the server refuses on submit — the
 * offers-what-it-refuses defect, reached from the free-text side. So the
 * screens warn BEFORE the save. A warning, never a block: the field stays free
 * text, submit stays enabled, and the server still decides.
 *
 * D17 RULED ON EXACTLY THIS POSITION, and it is the reason the shape above is
 * now fixed rather than preferred: "the box stays a text box and warns you
 * BEFORE you save rather than refusing you after. That way the product never
 * offers something it will then reject." Turning this into a block, or moving
 * it after the submit, would undo a ruling.
 *
 * (The citation this comment used to carry — "MD-08 and FIN-09 field 5 are
 * Short text" — was half wrong: MD-08's warehouse currency is field 15, "Pick
 * from list (Currencies)". `currency-guard.ts` records the full measurement of
 * which two boxes the spec actually types as free text and why neither gap is
 * D16/D17's to close.)
 *
 * WHY IT TAKES `codes` RATHER THAN READING THE TABLE ITSELF, which is the
 * whole safety property: `useCurrencyPickerCodes` APPENDS the value the record
 * already holds when the table no longer offers it (BR-MD-01-07 — what a
 * picker OFFERS and what it DISPLAYS are different questions). So a warehouse
 * locked to a since-switched-off `EUR` finds `EUR` in `codes` and stays quiet.
 * Rebuild this against `useCurrencyTable().rows` and it starts warning about
 * currencies the user lawfully holds, which is the same defect class arrived
 * at from a third direction.
 *
 * Quiet on empty and on a partial code: a blank is the shape check's business
 * (`wh.errCurrency`), and firing on `J` and `JP` would warn on the way to every
 * correct answer.
 *
 * WORDING (`fx.currencyNotYours`) IS APPROVED — D17, 26 Aug 2026, as proposed
 * and unchanged: "JPY is not one of your currencies. Add it on the Currencies
 * screen first." It was pending when this comment was written; it is settled
 * now and must not drift, here or in either dictionary. It still carries no
 * `ERR-` id on purpose: the spec contains no sentence for this, an approved
 * sentence is not a specified one, and inventing an id is the dangling
 * citation `rule-coverage.spec.ts` exists to catch.
 *
 * D16 SHARPENED WHAT THE SECOND HALF OF IT MEANS. "Add it on the Currencies
 * screen first" used to be advice that could fail: before the list opened, a
 * user told to add `JPY` would go to the Currencies screen and be refused
 * there too, because the Add box offered only the seven. The sentence now
 * describes something the user can actually do.
 */
export function currencyNotOffered(value: string, codes: readonly string[]): boolean {
  const code = value.trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(code)) return false;
  return !codes.includes(code);
}

/** MD-11 §6 "Switch off" / "Switch on" — BR-DATA-05's somewhere-to-go. */
export function useSetCurrencyActive() {
  return useFxMutation(
    async (input: { currencyId: string; isActive: boolean }): Promise<CurrencyRow> => {
      const response = input.isActive
        ? await api.fx.reactivateCurrency({ params: { currencyId: input.currencyId }, body: {} })
        : await api.fx.deactivateCurrency({ params: { currencyId: input.currencyId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  );
}

/** §17.3.2's delete — refused with ERR-MD-11-07 while anything names it. */
export function useRemoveCurrency() {
  return useFxMutation(async (currencyId: string): Promise<void> => {
    const response = await api.fx.removeCurrency({ params: { currencyId } });
    if (response.status !== 204) throwApiError(response.status, response.body);
  });
}

function useFxMutation<TInput, TOut>(run: (input: TInput) => Promise<TOut>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: FX_KEY }),
  });
}

export function useAddCurrency() {
  return useFxMutation(async (body: AddCurrencyRequest): Promise<CurrencyRow> => {
    const response = await api.fx.addCurrency({ body });
    if (response.status !== 201) throwApiError(response.status, response.body);
    return response.body;
  });
}

export function useSetCurrencySource() {
  return useFxMutation(
    async (input: { currencyId: string; source: 'manual' | 'live' }): Promise<CurrencyRow> => {
      const response = await api.fx.updateCurrencySource({
        params: { currencyId: input.currencyId },
        body: { source: input.source },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  );
}

export function useSetManualRate() {
  return useFxMutation(
    async (input: { currencyId: string; body: SetManualRateRequest }): Promise<CurrencyRow> => {
      const response = await api.fx.setManualRate({
        params: { currencyId: input.currencyId },
        body: input.body,
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  );
}

export function useFetchLiveRates() {
  return useFxMutation(async (_input: void): Promise<FetchRatesResult> => {
    const response = await api.fx.fetchLiveRates({ body: {} });
    if (response.status !== 200) throwApiError(response.status, response.body);
    return response.body;
  });
}

export function useSetBaseCurrency() {
  return useFxMutation(async (code: string): Promise<CurrencyTableResponse> => {
    const response = await api.fx.setBaseCurrency({ body: { code } });
    if (response.status !== 200) throwApiError(response.status, response.body);
    return response.body;
  });
}

export function useRateHistory(
  currencyId: string | null,
  range: { from?: string; to?: string } = {},
) {
  return useQuery({
    queryKey: [...FX_KEY, 'history', currencyId, range],
    enabled: Boolean(currencyId),
    queryFn: async (): Promise<{ code: string; points: RateHistoryPoint[] }> => {
      const response = await api.fx.getRateHistory({
        params: { currencyId: currencyId! },
        query: {
          ...(range.from ? { from: range.from } : {}),
          ...(range.to ? { to: range.to } : {}),
        },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useRevaluationPreview(date: string) {
  return useQuery({
    queryKey: [...FX_KEY, 'revaluation', date],
    queryFn: async (): Promise<RevaluationPreviewResponse> => {
      const response = await api.fx.getRevaluationPreview({
        query: date === '' ? {} : { date },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

/** FIN-05's posted history, one page at a time (NFR-42). */
export function useRevaluations(page = 1, pageSize = DEFAULT_PAGE_SIZE) {
  return useQuery({
    queryKey: [...FX_KEY, 'revaluations', page, pageSize],
    queryFn: async () => {
      const response = await api.fx.listRevaluations({ query: { page, pageSize } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

export function usePostRevaluation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: PostRevaluationRequest): Promise<RevaluationResponse> => {
      const response = await api.fx.postRevaluation({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FX_KEY });
      // The revaluation posts a journal, so the chart's balances moved.
      void queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY });
    },
  });
}
